import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";
import { StatCards } from "@/components/admin/StatCards";
import { OverviewTab } from "@/components/admin/OverviewTab";
import { VideosTab } from "@/components/admin/VideosTab";
import { ImportTab } from "@/components/admin/ImportTab";
import { ArchiveTab } from "@/components/admin/ArchiveTab";
import { NewsletterTab } from "@/components/admin/NewsletterTab";
import { MembersTab } from "@/components/admin/MembersTab";
import { SubmissionsTab } from "@/components/admin/SubmissionsTab";

const EMPTY = { title: "", original_title: "", youtube_id: "", subject: "General", recording_date: "", location: "", duration: "", description: "", source: "", collection: "", notes: "", copyright: "", tags: "", content_type: "", areas_of_study: "", verification_status: "", source_status: "", collection_group: "", uploader: "", videographer: "", media_owner: "", source_archive: "", rights_holder: "" };
const EMPTY_UPLOAD = { title: "", subject: "General", recording_date: "", location: "", duration: "", source: "", description: "", tags: "" };
const TABS = ["overview", "videos", "import", "archive", "submissions", "newsletter", "members"];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  const [tab, setTab] = useState("overview");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [vq, setVq] = useState("");
  const [allowlist, setAllowlist] = useState("");
  const [promoteEmail, setPromoteEmail] = useState("");
  const [admins, setAdmins] = useState([]);
  const [members, setMembers] = useState([]);
  const [mq, setMq] = useState("");
  const [archival, setArchival] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [contribCounts, setContribCounts] = useState({});
  const [contribFilter, setContribFilter] = useState("pending");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadMeta, setUploadMeta] = useState(EMPTY_UPLOAD);
  const [importing, setImporting] = useState(false);
  const [ytText, setYtText] = useState("");
  const [ytBusy, setYtBusy] = useState(false);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const videoFileRef = useRef(null);
  const fileRef = useRef(null);
  const catFileRef = useRef(null);
  const xlsxRef = useRef(null);
  const formRef = useRef(null);

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  const loadStats = useCallback(async () => {
    try { const s = await api.get("/admin/stats"); setStats(s.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);

  const loadVideos = useCallback(async () => {
    const p = new URLSearchParams({ sort: "newest", limit: 60 });
    if (vq) p.set("q", vq);
    const v = await api.get(`/videos?${p.toString()}`);
    setVideos(v.data.videos);
  }, [vq]);

  const loadAdmins = useCallback(async () => {
    try { const { data } = await api.get("/admin/admins"); setAdmins(data.admins); }
    catch (e) { console.error("Failed to load admins:", e); toast.error("Unable to load administrators"); }
  }, []);

  const loadMembers = useCallback(async () => {
    try { const { data } = await api.get("/admin/members"); setMembers(data.members); }
    catch (e) { console.error("Failed to load members:", e); toast.error("Unable to load members"); }
  }, []);

  const loadArchival = useCallback(async () => {
    try { const { data } = await api.get("/admin/archival"); setArchival(data); }
    catch (e) { console.error("Failed to load archival records:", e); toast.error("Unable to load archival records"); }
  }, []);

  const loadContributions = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/contributions?status=${contribFilter}`);
      setContributions(data.contributions); setContribCounts(data.counts);
    } catch (e) { console.error("Failed to load submissions:", e); toast.error("Unable to load submissions"); }
  }, [contribFilter]);

  const updateContribution = async (cid, status) => {
    try {
      await api.patch(`/admin/contributions/${cid}`, { status });
      toast.success(`Marked ${status}`);
      loadContributions(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(loadVideos, 250); return () => clearTimeout(t); }, [loadVideos]);
  useEffect(() => { if (tab === "members") { loadAdmins(); loadMembers(); } }, [tab, loadAdmins, loadMembers]);
  useEffect(() => { if (tab === "archive") loadArchival(); }, [tab, loadArchival]);
  useEffect(() => { if (tab === "submissions") loadContributions(); }, [tab, loadContributions]);

  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: parseInt((form.recording_date.match(/\d{4}/) || [])[0]) || null,
      tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      areas_of_study: form.areas_of_study ? form.areas_of_study.split(",").map((t) => t.trim()).filter(Boolean) : [],
    };
    try {
      if (editingId) {
        await api.put(`/admin/videos/${editingId}`, payload);
        toast.success("Video updated");
      } else {
        await api.post("/admin/videos", payload);
        toast.success("Video added to the library");
      }
      resetForm(); loadVideos(); loadStats();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  const startEdit = async (id) => {
    try {
      const { data } = await api.get(`/admin/videos/${id}`);
      setForm({
        title: data.title || "", original_title: data.original_title || "", youtube_id: data.youtube_id || "", subject: data.subject || "General",
        recording_date: data.recording_date || "", location: data.location || "", duration: data.duration || "",
        description: data.description || "", source: data.source || "", collection: data.collection || "",
        notes: data.notes || "", copyright: data.copyright || "", tags: (data.tags || []).join(", "),
        content_type: data.content_type || "", areas_of_study: (data.areas_of_study || []).join(", "),
        verification_status: data.verification_status || "", source_status: data.source_status || "",
        collection_group: data.collection_group || "",
        uploader: data.uploader || "", videographer: data.videographer || "", media_owner: data.media_owner || "",
        source_archive: data.source_archive || "", rights_holder: data.rights_holder || "",
      });
      setEditingId(id);
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    await api.delete(`/admin/videos/${id}`);
    if (editingId === id) resetForm();
    toast.success("Removed"); loadVideos(); loadStats();
  };

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const { data } = await api.post("/admin/videos/bulk", { csv: text });
      toast.success(`Imported ${data.added} videos (${data.skipped} skipped). Library now has ${data.total_now}.`);
      loadVideos(); loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const bulkYoutube = async () => {
    if (!ytText.trim()) { toast.error("Paste at least one YouTube link"); return; }
    setYtBusy(true);
    try {
      const { data } = await api.post("/admin/videos/bulk-youtube", { text: ytText });
      const more = data.remaining ? ` · ${data.remaining} more remaining — run again to continue` : "";
      toast.success(`Added ${data.added}, skipped ${data.skipped} · library now ${data.total_now}${more}`);
      setYtText(""); loadStats(); loadVideos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk add failed"); }
    finally { setYtBusy(false); }
  };

  const exportCatalogue = async () => {
    try {
      const { data } = await api.get("/admin/catalogue/export");
      downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), `chiba-catalogue-${data.count}.json`);
      toast.success(`Exported ${data.count} records`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const importCatalogue = async (file) => {
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const videos = parsed.videos || parsed;
      const { data } = await api.post("/admin/catalogue/import", { videos });
      toast.success(`Synced: ${data.inserted} added, ${data.updated} updated · total ${data.total_now}`);
      loadStats(); loadVideos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed — is this a valid catalogue export?"); }
    finally { if (catFileRef.current) catFileRef.current.value = ""; }
  };

  const exportMembers = async () => {
    try {
      const res = await api.get("/admin/members/export", { responseType: "blob" });
      downloadBlob(res.data, "chiba-archive-members.csv");
      toast.success("Members CSV downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };

  const importSpreadsheet = async (file) => {
    if (!file) return;
    setXlsxBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/videos/import-spreadsheet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.inserted} added, ${data.updated} updated · ${data.total_now} total (${data.streamable} streamable)`);
      loadStats(); loadVideos();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Spreadsheet import failed"); }
    finally { setXlsxBusy(false); if (xlsxRef.current) xlsxRef.current.value = ""; }
  };

  const uploadVideoFile = async (file) => {
    if (!file) return;
    if (!uploadMeta.title.trim()) { toast.error("Please enter a title before uploading"); return; }
    setUploading(true); setUploadPct(0);
    try {
      const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
      const { data: init } = await api.post("/admin/uploads/init", { filename: file.name });
      const uploadId = init.upload_id;
      const CHUNK = 5 * 1024 * 1024;
      let sent = 0;
      for (let start = 0; start < file.size; start += CHUNK) {
        const blob = file.slice(start, Math.min(start + CHUNK, file.size));
        const buf = await blob.arrayBuffer();
        await api.post(`/admin/uploads/${uploadId}/chunk`, buf, { headers: { "Content-Type": "application/octet-stream" } });
        sent += blob.size;
        setUploadPct(Math.round((sent / file.size) * 100));
      }
      await api.post(`/admin/uploads/${uploadId}/finalize`, { ext, ...uploadMeta });
      toast.success(`Uploaded "${uploadMeta.title}" — now streaming in the library`);
      setUploadMeta(EMPTY_UPLOAD);
      if (videoFileRef.current) videoFileRef.current.value = "";
      loadStats(); loadVideos();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); setUploadPct(0); }
  };

  const sendNewsletter = async () => {
    try {
      const { data } = await api.post("/admin/newsletter/send");
      if (data.error) toast.error(data.error);
      else toast.success(`Prepared ${data.count} video${data.count > 1 ? "s" : ""} · ${data.delivered ? `emailed ${data.sent}` : "email skipped (no SendGrid key)"}`);
      loadStats();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAllowlist = async () => {
    const emails = allowlist.split(/[\n,]/).map((e) => e.trim()).filter(Boolean);
    try {
      const { data } = await api.post("/admin/members/allowlist", { emails });
      toast.success(`Saved ${data.count} member emails`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const promote = async () => {
    const email = promoteEmail.trim();
    if (!email) return;
    try {
      await api.post("/admin/promote", { email, admin: true });
      toast.success(`${email} is now an administrator`);
      setPromoteEmail(""); loadAdmins(); loadMembers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const demote = async (email) => {
    try {
      await api.post("/admin/promote", { email, admin: false });
      toast.success(`${email} is no longer an administrator`); loadAdmins(); loadMembers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-12">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">Administration</p>
        <h1 className="font-serif text-5xl text-[#EBE9E1] mb-10">Archive Control Room</h1>

        <StatCards stats={stats} />

        <div className="flex gap-6 border-b border-white/10 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
              className={`pb-3 text-sm capitalize whitespace-nowrap transition-colors ${tab === t ? "text-[#C5A059] border-b-2 border-[#C5A059]" : "text-[#8A8F98]"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab stats={stats} />}
        {tab === "videos" && (
          <VideosTab form={form} setForm={setForm} editingId={editingId} resetForm={resetForm}
            submit={submit} formRef={formRef} vq={vq} setVq={setVq} videos={videos} startEdit={startEdit} del={del} />
        )}
        {tab === "import" && (
          <ImportTab uploadMeta={uploadMeta} setUploadMeta={setUploadMeta} uploading={uploading} uploadPct={uploadPct}
            videoFileRef={videoFileRef} uploadVideoFile={uploadVideoFile} fileRef={fileRef} importing={importing} doImport={doImport}
            ytText={ytText} setYtText={setYtText} ytBusy={ytBusy} bulkYoutube={bulkYoutube}
            exportCatalogue={exportCatalogue} importCatalogue={importCatalogue} catFileRef={catFileRef}
            xlsxRef={xlsxRef} xlsxBusy={xlsxBusy} importSpreadsheet={importSpreadsheet} />
        )}
        {tab === "archive" && <ArchiveTab archival={archival} />}
        {tab === "submissions" && (
          <SubmissionsTab contributions={contributions} counts={contribCounts}
            filter={contribFilter} setFilter={setContribFilter} updateContribution={updateContribution} />
        )}
        {tab === "newsletter" && <NewsletterTab sendNewsletter={sendNewsletter} />}
        {tab === "members" && (
          <MembersTab members={members} mq={mq} setMq={setMq} admins={admins} promoteEmail={promoteEmail}
            setPromoteEmail={setPromoteEmail} promote={promote} demote={demote} allowlist={allowlist}
            setAllowlist={setAllowlist} saveAllowlist={saveAllowlist} exportMembers={exportMembers} />
        )}
      </div>
    </div>
  );
}
